import LaunchIcon from "@mui/icons-material/Launch";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import isEqual from "lodash.isequal";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  addBidChoiceEvent,
  fetchLatestBidChoiceEventByReferenceNumberAndEid,
  fetchLatestBidLineEventsByReferenceNumber,
} from "../../api/bid";
import { isUserAuthorizedByServer } from "../../api/userAccess";
import ViewCommentsDialog from "../../components/ViewCommentsDialog";
import StarsCloseIconButton from "../../components/fundamentals/StarsCloseIconButton";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { logout } from "../../util/authentication";
import { BID_UPDATE_TYPE, EMPLOYEE_BID_STATUS } from "../../util/bid/bidConstants";
import { buildBidChoiceEvent, prepareNewBidChoiceEventPayload } from "../../util/bid/bidEventBuilder";
import { getScrubbedBidEvent } from "../../util/bid/bidScrubber";
import { filterBidLines, getDisplayedBidLinesWithRadioFilter } from "../../util/bid/filterBidLines";
import { filterAndSortSelectedBidLines } from "../../util/bid/filterBids";
import {
  getBidName,
  getNumberOfMyChoicesLabel,
  getShiftDaysOff,
  getShiftPositions,
  getShiftStartTimes,
  getShiftAssignments,
} from "../../util/bid/shiftBidUtils";
import { sortBidDaysOffOptions } from "../../util/bid/sortBids";
import { sortOnCaseSensitiveTexts } from "../../util/commonUtils.js";
import {
  ADMIN_BID_CHOICE_CHAR_LIMIT,
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  EMPLOYEE_ID,
  FEATURES,
  HOME_PATH,
  STATION,
} from "../../util/constants.js";
import {
  BID_CHOICE_SAVE_ERROR_MESSAGE,
  BID_FORM_CANCEL_MESSAGE,
  BID_FORM_CLEAR_ALL,
  BID_FORM_CLEAR_ALL_CHOICES_MESSAGE,
  BID_FORM_GUIDE_MESSAGE,
  CHAR_LIMIT_MESSAGE,
  COMMENTS_REQUIRED,
  DAYS_OFF_FILTER_MUST_BE_SELECTED,
  ASSIGNMENT_FILTER_MUST_BE_SELECTED,
  LAST_UPDATED,
  LAST_UPDATED_TIMESTAMP,
  POSITION_FILTER_MUST_BE_SELECTED,
  SHIFT_BID_CHOICE_CONFIRMATION_MESSAGE,
  SHIFT_BID_CHOICE_CONFIRMATION_TITLE,
  START_TIME_FILTER_MUST_BE_SELECTED,
} from "../../util/messageConstants";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../../util/timeZoneService";
import { getWorkgroupGivenJobCode } from "../../util/workgroupMapper";
import PageLoading from "../PageLoading";
import UnauthorizedMessage from "../UnauthorizedMessage";
import ConfirmButton from "../fundamentals/ConfirmButton";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import InfoDialog from "../fundamentals/InfoDialog";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import StarsBreadcrumbs from "../fundamentals/StarsBreadcrumbs";
import StarsSelectInput from "../fundamentals/StarsSelectInput";
import SuccessDialog from "../fundamentals/SuccessDialog";
import BidTimer from "./BidTimer";
import ShiftBidChoicesTable from "./ShiftBidChoicesTable";
import ShiftBidReviewTable from "./ShiftBidReviewTable";
import PropTypes from "prop-types";

const useStyles = makeStyles((theme) => ({
  filterInputs: {
    width: "100%",
    textAlign: "left",
    backgroundColor: theme.palette.secondary.swaWhite,
  },
  inputText: {
    width: "15vw",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  employeeDataTitle: {
    marginTop: "7px",
    color: theme.palette.secondary.swaGray,
  },
  employeeDataValue: {
    fontSize: "16px",
    marginTop: "-5px",
  },
  lastUpdated: {
    textAlign: "left",
    color: theme.palette.secondary.swaGray4,
    marginRight: "3.5vw",
  },
  commentBox: {
    width: "80%",
    margin: "10px auto",
  },
}));

const ManageShiftBidChoices = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isBidLinesLoading, setIsBidLinesLoading] = useState(true);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  // Bid Choice Data State
  const [bidChoiceData, setBidChoiceData] = useState(null);
  const [bidLines, setBidLines] = useState([]); // Original bid lines from the database
  const [filteredBidLines, setFilteredBidLines] = useState([]);
  const [displayedBidLines, setDisplayedBidLines] = useState([]);
  const [selectedBidLines, setSelectedBidLines] = useState([]);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [initialSelectedChoices, setInitialSelectedChoices] = useState([]);

  // Filter Input State
  const [filteredPositions, setFilteredPositions] = useState({});
  const [filteredAssignments, setFilteredAssignments] = useState({});
  const [filteredDaysOff, setFilteredDaysOff] = useState({});
  const [filteredStartTimes, setFilteredStartTimes] = useState({});
  const [filterRadioButton, setFilterRadioButton] = useState("showAll");
  const [numOfFilteredMyChoices, setNumOfFilteredMyChoices] = useState(0);

  // Dialog state
  const [isClearAllDialogOpen, setIsClearAllDialogOpen] = useState(false);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);
  const [isEnlargeBidLineDialogOpen, setIsEnlargeBidLineDialogOpen] = useState(false);

  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isSubmitResponseDialogOpen, setIsSubmitResponseDialogOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [submitErrors, setSubmitErrors] = useState("");
  const [isReviewPage, setIsReviewPage] = useState(false);
  const [lastUpdatedByFirstName, setLastUpdatedByFirstName] = useState("");
  const [lastUpdatedByLastName, setLastUpdatedByLastName] = useState("");
  const [viewCommentsDialogOpen, setViewCommentsDialogOpen] = useState(false);
  const [lastUpdatedComments, setLastUpdatedComments] = useState("");
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState(false);
  const [commentErrorMessage, setCommentErrorMessage] = useState("");

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else if (props.selectedBidGroupEmployeeData) {
      fetchLatestShiftBidChoices(props.selectedBidGroupEmployeeData.eid, props.referenceNumber);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState, props.selectedBidGroupEmployeeData]);

  useEffect(() => {
    applyRadioFilters();
  }, [selectedChoices, filterRadioButton, filteredBidLines]); // eslint-disable-line react-hooks/exhaustive-deps

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const handleCommentsChange = (comments) => {
    setCommentsError(false);
    setCommentErrorMessage("");
    setComments(comments.target.value);
  };

  const fetchLatestShiftBidChoices = (eid, referenceNumber) => {
    let latestBidChoice;
    fetchLatestBidChoiceEventByReferenceNumberAndEid(eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response?.event) {
          const bidChoiceEvent = getScrubbedBidEvent(response.event);
          const userFtPt = response.event.ftpt;
          setBidChoiceData(bidChoiceEvent);
          latestBidChoice = response.event.choices;
          setSelectedChoices(response.event?.choices ? response.event.choices : []);
          setInitialSelectedChoices(response.event?.choices ? response.event.choices : []);
          setNumOfFilteredMyChoices(response.event?.choices?.length);
          if (response.event?.modifiedByFirstName && response.event?.modifiedByLastName) {
            setLastUpdatedByFirstName(response.event?.modifiedByFirstName);
            setLastUpdatedByLastName(response.event?.modifiedByLastName);
          }
          setLastUpdatedComments(response.event?.comments);
          fetchLatestBidLineEventsByReferenceNumber(userSession.eid, referenceNumber)
            .then((res) => res.json())
            .then((response) => {
              if (response?.events) {
                const initFtPtBidLines = response.events
                  .filter((event) => event?.ftpt?.toLowerCase() === userFtPt.toLowerCase() && !event?.isDupedLine)
                  .map((event) => {
                    event.id = event.lineNumber;
                    return event;
                  });
                setBidLines(initFtPtBidLines);
                setFilteredBidLines(initFtPtBidLines);
                setDisplayedBidLines(initFtPtBidLines);
                if (props.useAssignmentForEmp) {
                  const shiftAssignments = getShiftAssignments(initFtPtBidLines);
                  setFilteredAssignments(Object.values(shiftAssignments));
                } else {
                  const shiftPositions = getShiftPositions(initFtPtBidLines);
                  setFilteredPositions(Object.values(shiftPositions));
                }
                const shiftDaysOff = getShiftDaysOff(initFtPtBidLines);
                setFilteredDaysOff(Object.values(shiftDaysOff));

                const shiftStartTimes = getShiftStartTimes(initFtPtBidLines);
                setFilteredStartTimes(Object.values(shiftStartTimes));

                if (props.isAwardedEmpReadOnlyView) {
                  setSelectedBidLines(filterAndSortSelectedBidLines(initFtPtBidLines, latestBidChoice));
                }

                setIsPageLoading(false);
                setIsBidLinesLoading(false);
              } else {
                throw Error();
              }
            })
            .catch((err) => {
              console.error(`Bid Line Error: %j`, err);
              setBidLines([]);
              setIsBidLinesLoading(false);
            });
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
        setIsPageLoading(false);
      });
  };

  const handleSubmitClicked = () => {
    if (comments === "") {
      setCommentsError(true);
      setCommentErrorMessage(COMMENTS_REQUIRED);
    } else if (comments.length > ADMIN_BID_CHOICE_CHAR_LIMIT) {
      setCommentsError(true);
      setCommentErrorMessage(CHAR_LIMIT_MESSAGE(ADMIN_BID_CHOICE_CHAR_LIMIT));
    } else {
      setIsSubmitLoading(true);
      console.log("Saving bid choice data...");
      let payload = buildBidChoiceEvent(
        prepareNewBidChoiceEventPayload({ bidChoiceData, selectedChoices }),
        userSession,
        comments
      );
      addBidChoiceEvent(payload)
        .then((res) => res.json())
        .then((result) => {
          if (result.message) {
            console.error("Error: ", result.message);
            setSubmitErrors(result.message);
            setIsSubmitLoading(false);
            setIsSubmitDialogOpen(false);
            setIsSubmitResponseDialogOpen(true);
          } else {
            console.log("Success!");
            props.setIsBidGroupTabChanged(true);
            setIsSubmitDialogOpen(false);
            setIsSubmitLoading(false);
            setIsSubmitResponseDialogOpen(true);
          }
        })
        .catch((err) => {
          console.log("err", err);
          setIsSubmitDialogOpen(false);
          setIsSubmitLoading(false);
        });
    }
  };

  const handleClickSubmitDialogOpen = () => {
    setIsSubmitDialogOpen(true);
  };

  const handleClickSubmitDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSubmitDialogOpen(false);
  };

  const handleClickCancelDialogOpen = () => {
    if (!isEqual(selectedChoices, initialSelectedChoices)) {
      setIsExitDialogOpen(true);
    } else {
      // no changes made, so move away from manage shift bid choices page
      props.setIsManageShiftBidChoicesSelected(false);
    }
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsExitDialogOpen(false);
  };

  const handleClickGuideDialogOpen = () => {
    setIsGuideDialogOpen(true);
  };

  const handleCloseClickedGuideDialog = () => {
    setIsGuideDialogOpen(false);
  };

  const handleReviewButtonClick = () => {
    setIsReviewPage(true);
    setSelectedBidLines(filterAndSortSelectedBidLines(bidLines, selectedChoices));
  };

  const handleClickClearAllDialogOpen = () => {
    setIsClearAllDialogOpen(true);
  };

  const handleClickCancelClearAllDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsClearAllDialogOpen(false);
  };

  const handleClearAllChoices = () => {
    setSelectedChoices([]);
    setSelectedBidLines([]);
    setNumOfFilteredMyChoices(0);
    setIsClearAllDialogOpen(false);
    setFilterRadioButton("showAll");
  };

  const applyFilters = (newFilteredBidLines) => {
    setFilteredBidLines(newFilteredBidLines);
    setNumOfFilteredMyChoices(getNumberOfMyChoicesLabel(newFilteredBidLines, selectedChoices));
  };

  const applyRadioFilters = () => {
    setNumOfFilteredMyChoices(getNumberOfMyChoicesLabel(filteredBidLines, selectedChoices));

    const newDisplayedBidLines = getDisplayedBidLinesWithRadioFilter(
      filteredBidLines,
      selectedChoices,
      filterRadioButton
    );
    setDisplayedBidLines(newDisplayedBidLines);
  };

  const handlePositionFilterChange = (event) => {
    setFilteredPositions(Object.values(event.target.value));

    const newFilteredBidLines = filterBidLines(
      bidLines,
      Object.values(event.target.value),
      filteredAssignments,
      filteredDaysOff,
      filteredStartTimes,
      props.useAssignmentForEmp
    );
    applyFilters(newFilteredBidLines);
  };

  const handleAssignmentFilterChange = (event) => {
    setFilteredAssignments(Object.values(event.target.value));
    const newFilteredBidLines = filterBidLines(
      bidLines,
      filteredPositions,
      Object.values(event.target.value),
      filteredDaysOff,
      filteredStartTimes,
      props.useAssignmentForEmp
    );
    applyFilters(newFilteredBidLines);
  };

  const handleDaysOffFilterChange = (event) => {
    setFilteredDaysOff(Object.values(event.target.value));
    const newFilteredBidLines = filterBidLines(
      bidLines,
      filteredPositions,
      filteredAssignments,
      Object.values(event.target.value),
      filteredStartTimes,
      props.useAssignmentForEmp
    );
    applyFilters(newFilteredBidLines);
  };

  const handleStartTimesFilterChange = (event) => {
    setFilteredStartTimes(Object.values(event.target.value));
    const newFilteredBidLines = filterBidLines(
      bidLines,
      filteredPositions,
      filteredAssignments,
      filteredDaysOff,
      Object.values(event.target.value),
      props.useAssignmentForEmp
    );
    applyFilters(newFilteredBidLines);
  };

  const handleAllSelectRadioChange = () => {
    setFilterRadioButton("showAll");
  };

  const handleMyChoicesRadioChange = () => {
    setFilterRadioButton("myChoices");
  };

  const handleUnSelectedRadioChange = () => {
    setFilterRadioButton("unselected");
  };

  const handleResetFiltersClick = () => {
    const shiftPositions = getShiftPositions(bidLines);
    setFilteredPositions(Object.values(shiftPositions));

    const shiftAssignments = getShiftAssignments(bidLines);
    setFilteredAssignments(Object.values(shiftAssignments));

    const shiftDaysOff = getShiftDaysOff(bidLines);
    setFilteredDaysOff(Object.values(shiftDaysOff));

    const shiftStartTimes = getShiftStartTimes(bidLines);
    setFilteredStartTimes(Object.values(shiftStartTimes));

    const newFilteredBidLines = filterBidLines(
      bidLines,
      Object.values(shiftPositions),
      Object.values(shiftAssignments),
      Object.values(shiftDaysOff),
      Object.values(shiftStartTimes),
      props.useAssignmentForEmp
    );
    applyFilters(newFilteredBidLines);
    setFilterRadioButton("showAll");
  };

  const renderPositions = () => {
    return (
      <StarsSelectInput
        label="Positions"
        menuMessage="Filter on Positions"
        value={filteredPositions}
        allOptions={Object.values(getShiftPositions(bidLines))}
        sortComparator={sortOnCaseSensitiveTexts}
        fullWidth
        onChange={(event) => handlePositionFilterChange(event)}
        errors={filteredPositions.length <= 0 && bidLines.length > 0 ? POSITION_FILTER_MUST_BE_SELECTED : ""}
        className={classes.filterInputs}
        multiple={true}
      />
    );
  };

  const renderAssignments = () => {
    return (
      <StarsSelectInput
        label="Assignments"
        menuMessage="Filter on Assignments"
        value={filteredAssignments}
        allOptions={Object.values(getShiftAssignments(bidLines))}
        sortComparator={sortOnCaseSensitiveTexts}
        fullWidth
        onChange={(event) => handleAssignmentFilterChange(event)}
        errors={filteredAssignments.length <= 0 && bidLines.length > 0 ? ASSIGNMENT_FILTER_MUST_BE_SELECTED : ""}
        className={classes.filterInputs}
        multiple={true}
      />
    );
  };

  const renderDaysOff = () => {
    return (
      <StarsSelectInput
        label="Days Off"
        menuMessage="Filter on Days Off"
        value={filteredDaysOff}
        allOptions={sortBidDaysOffOptions(Object.values(getShiftDaysOff(bidLines)))}
        fullWidth
        onChange={(event) => handleDaysOffFilterChange(event)}
        errors={filteredDaysOff.length <= 0 && bidLines.length > 0 ? DAYS_OFF_FILTER_MUST_BE_SELECTED : ""}
        className={classes.filterInputs}
        multiple={true}
      />
    );
  };

  const renderStartTimes = () => {
    return (
      <StarsSelectInput
        label="Start Times"
        menuMessage="Filter on Start Times"
        value={filteredStartTimes}
        allOptions={Object.values(getShiftStartTimes(bidLines))}
        sortComparator={sortOnCaseSensitiveTexts}
        fullWidth
        onChange={(event) => handleStartTimesFilterChange(event)}
        errors={filteredStartTimes.length <= 0 && bidLines.length > 0 ? START_TIME_FILTER_MUST_BE_SELECTED : ""}
        className={classes.filterInputs}
        multiple={true}
      />
    );
  };

  const renderComments = () => {
    return (
      <>
        <TextField
          className={classes.commentBox}
          value={comments}
          variant="outlined"
          onChange={handleCommentsChange}
          label="Comments (Mandatory)"
          multiline
          error={commentsError}
          helperText={commentErrorMessage}
        />

        {comments.length > ADMIN_BID_CHOICE_CHAR_LIMIT ? (
          <Box sx={{ textAlign: "right", color: "primary.swaRed", width: "90%" }}>
            {comments.length} / {ADMIN_BID_CHOICE_CHAR_LIMIT}
          </Box>
        ) : (
          <Box sx={{ textAlign: "right", width: "90%" }}>
            {comments.length} / {ADMIN_BID_CHOICE_CHAR_LIMIT}
          </Box>
        )}
      </>
    );
  };

  const renderSubmitShiftBidChoiceDialog = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        sx={{ margin: "auto" }}
        open={isSubmitDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
      >
        {selectedChoices?.length === 0 ? (
          <>
            <DialogTitle>Submit a Blank Bid</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>You are submitting a blank bid.</div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                </Grid>
              </DialogContentText>
            </DialogContent>
            {renderComments()}
          </>
        ) : (
          <>
            <DialogTitle>Submit Bid Choices</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>
                    You are submitting choices for <b>{selectedChoices?.length}</b> of <b>{bidLines?.length}</b> bid
                    lines.
                  </div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                  <div style={{ marginTop: "10px" }}>
                    You will see your submitted choices the next time you open this bid form again.
                  </div>
                </Grid>
              </DialogContentText>
            </DialogContent>
            {renderComments()}
          </>
        )}
        <DialogActions>
          <ConfirmButton variant="outlined" color="primary" onClick={handleClickSubmitDialogClose}>
            Cancel
          </ConfirmButton>
          <ConfirmButton variant="contained" color="primary" onClick={handleSubmitClicked} isLoading={isSubmitLoading}>
            Submit
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  const getLastSubmittedTimestamp = () => {
    return bidChoiceData?.createdAt ? Date.parse(bidChoiceData.createdAt) : "";
  };

  const getBidAdminPath = () => {
    return props.featureConfig.childItems.find((childItem) => childItem.key === FEATURES.BID_ADMINISTRATION).path;
  };

  const handleEnlargeBidLinesButtonClicked = (event) => {
    setIsEnlargeBidLineDialogOpen(true);
  };

  const handleEnlargeBidLinesDialogClosed = (event) => {
    setIsEnlargeBidLineDialogOpen(false);
  };

  const renderEnlargeBidLinesDialog = () => {
    return (
      <Dialog onClose={handleEnlargeBidLinesDialogClosed} open={isEnlargeBidLineDialogOpen} fullScreen>
        {renderBidLines(true)}
      </Dialog>
    );
  };

  const getLastUpdatedTimeString = () => {
    return getStationTimeZoneDateTimeStringFromUTCInteger(getLastSubmittedTimestamp(), bidChoiceData.station).toFormat(
      "MM/dd/yyyy T ZZZZ"
    );
  };

  const renderBidLines = (isDialog) => {
    return (
      <>
        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
            backgroundColor: "secondary.swaLightestBlue",
            paddingLeft: "1rem",
            paddingRight: "1rem",
          }}
        >
          <Grid
            container
            item
            xs={12}
            sx={{ alignItems: "center", justifyContent: "space-between", paddingTop: "6px" }}
          >
            <Typography sx={{ fontWeight: "bold", color: "primary.swaBlue" }}>
              Showing {filteredBidLines.length} of {bidLines.length} {bidChoiceData.ftpt} bid lines
            </Typography>
            {isDialog ? (
              <Grid item xs={1} sx={{ textAlign: "right", paddingRight: "1rem" }}>
                <StarsCloseIconButton onClick={handleEnlargeBidLinesDialogClosed} />
              </Grid>
            ) : (
              <IconButton sx={{ padding: 0 }} onClick={handleEnlargeBidLinesButtonClicked}>
                <LaunchIcon color="primary" />
              </IconButton>
            )}
          </Grid>

          <Grid container item sx={{ alignItems: "center", direction: "row", justifyContent: "space-between" }}>
            <Grid container item xs={10} sx={{ padding: "1rem 1rem 1rem 0" }}>
              <Grid item xs={2}>
                {props.useAssignmentForEmp ? renderAssignments() : renderPositions()}
              </Grid>
              <Grid item xs={2} sx={{ marginLeft: "1rem" }}>
                {renderDaysOff()}
              </Grid>
              <Grid item xs={2} sx={{ marginLeft: "1rem" }}>
                {renderStartTimes()}
              </Grid>
            </Grid>
            <Grid item>
              <Button
                sx={{ fontWeight: "bold", textDecoration: "underline", padding: 0 }}
                onClick={() => handleResetFiltersClick()}
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
            backgroundColor: "secondary.swaGray1",
            paddingLeft: "1rem",
          }}
        >
          <Stack direction="row">
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "start" }}>
              <Typography sx={{ fontWeight: "bold", marginTop: "8px" }}>Bid Line Filter:</Typography>
              <Box sx={{ paddingLeft: "1rem" }}>
                <FormControl>
                  <RadioGroup row>
                    <FormControlLabel
                      value="all"
                      control={<Radio />}
                      label={`All (${filteredBidLines.length})`}
                      checked={filterRadioButton === "showAll"}
                      onChange={handleAllSelectRadioChange}
                    />
                    <FormControlLabel
                      value="myChoices"
                      control={<Radio />}
                      label={`My Choices (${numOfFilteredMyChoices})`}
                      checked={filterRadioButton === "myChoices"}
                      onChange={handleMyChoicesRadioChange}
                    />
                    <FormControlLabel
                      value="unselected"
                      control={<Radio />}
                      label={`Unselected (${filteredBidLines.length - numOfFilteredMyChoices})`}
                      checked={filterRadioButton === "unselected"}
                      onChange={handleUnSelectedRadioChange}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Grid>
          </Stack>
        </Grid>

        {/* Choices Entered: */}
        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
            backgroundColor: "secondary.swaGray2",
            paddingLeft: "1rem",
          }}
        >
          <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "6px" }}>
            <Typography sx={{ fontWeight: "bold" }}>
              {selectedChoices.length} {`Choice${selectedChoices.length > 1 ? "s" : ""}`} Entered
            </Typography>
          </Grid>

          <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "8px" }}>
            <Button sx={{ fontWeight: "bold", textDecoration: "underline" }} onClick={handleClickClearAllDialogOpen}>
              Clear All
            </Button>
            <ConfirmationDialog
              isDialogOpen={isClearAllDialogOpen}
              title={BID_FORM_CLEAR_ALL}
              message={BID_FORM_CLEAR_ALL_CHOICES_MESSAGE}
              handleNoButtonClicked={handleClickCancelClearAllDialogClose}
              handleYesButtonClicked={handleClearAllChoices}
              noTitle="Cancel"
              yesTitle="Clear All"
            />
          </Grid>
        </Grid>

        {/* Choice / Bid Line */}
        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
          }}
        >
          <Box
            sx={{
              height: isDialog ? `calc(85vh - 100px)` : `calc(85vh - 405px)`,
              width: "inherit",
              "& .choices.selected": { backgroundColor: "secondary.swaLightestBlue" },
              "& .add-remove-icons.blocked": { backgroundColor: "secondary.swaGray2" },
              "& .add-remove-icons.addIcon": { backgroundColor: "secondary.swaBlue" },
            }}
          >
            <ShiftBidChoicesTable
              filteredBidLines={displayedBidLines}
              isBidLinesLoading={isBidLinesLoading}
              selectedChoices={selectedChoices}
              setSelectedChoices={setSelectedChoices}
              isDesktopView={true}
              useAssignmentForEmp={props.useAssignmentForEmp}
            />
          </Box>
        </Grid>
      </>
    );
  };

  const breadcrumbsList = [
    BREADCRUMBS_TITLE.HOME,
    BREADCRUMBS_TITLE.BID_ADMINISTRATION,
    `${BREADCRUMBS_TITLE.MANAGE_SHIFT_BID} / ${props.selectedBidGroupEmployeeData.eid}`,
  ];
  const breadcrumbsLinks = [HOME_PATH, getBidAdminPath()];

  const renderShiftBidChoicesPage = () => {
    if (isPageLoading) {
      return <PageLoading />;
    } else {
      return (
        <>
          {isEnlargeBidLineDialogOpen && renderEnlargeBidLinesDialog()}
          <StarsBreadcrumbs
            breadcrumbsList={breadcrumbsList}
            breadcrumbsLinks={breadcrumbsLinks}
            isConfirmationRequired={!isEqual(selectedChoices, initialSelectedChoices)}
            cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS}
          />
          {/* Title & Cancel/Submit Buttons */}
          <Grid container>
            <Grid container justifyContent="space-between">
              <Grid item sx={{ paddingLeft: "2rem", paddingTop: "10px" }}>
                <Grid item>
                  <Typography variant="h5" component="div" align="left">
                    {getBidName(props.bidData)} Shift Bid
                  </Typography>
                  {getLastSubmittedTimestamp() !== "" &&
                  lastUpdatedByFirstName &&
                  lastUpdatedByLastName &&
                  lastUpdatedByFirstName !== "" &&
                  lastUpdatedByLastName !== "" ? (
                    <Grid className={classes.lastUpdated}>
                      {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString()} `}
                      {lastUpdatedComments !== "" ? (
                        <Link onClick={() => setViewCommentsDialogOpen(true)} component="button" variant="subtitle2">
                          (View comment)
                        </Link>
                      ) : (
                        ""
                      )}
                    </Grid>
                  ) : getLastSubmittedTimestamp() !== "" ? (
                    <Grid className={classes.lastUpdated}>
                      {`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString()} `}
                    </Grid>
                  ) : (
                    ""
                  )}

                  <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                    {bidChoiceData &&
                      bidChoiceData.windowStartDate !== null &&
                      bidChoiceData.windowEndDate !== null && (
                        <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "16px" }}>
                          <BidTimer
                            windowDates={[bidChoiceData.windowStartDate, bidChoiceData.windowEndDate]}
                            station={bidChoiceData.station}
                          />
                        </Typography>
                      )}
                  </Box>
                </Grid>
              </Grid>

              <Stack>
                <Grid item sx={{ display: "flex", paddingRight: "2rem" }}>
                  {isReviewPage ? (
                    <>
                      <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
                        <Button
                          sx={{ width: "100px" }}
                          variant="outlined"
                          color="primary"
                          onClick={handleClickCancelDialogOpen}
                        >
                          Cancel
                        </Button>
                      </Box>
                      <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
                        <Button
                          sx={{
                            width: "100px",
                          }}
                          variant="outlined"
                          color="primary"
                          onClick={() => setIsReviewPage(false)}
                        >
                          Back
                        </Button>
                      </Box>
                      <Box sx={{ paddingTop: "10px" }}>
                        <Button
                          sx={{ width: "100px" }}
                          variant="contained"
                          color="primary"
                          onClick={handleClickSubmitDialogOpen}
                          disabled={
                            bidChoiceData?.updateType === BID_UPDATE_TYPE.DELETE ||
                            bidChoiceData?.status === EMPLOYEE_BID_STATUS.WITHDRAWN ||
                            isEqual(selectedChoices, initialSelectedChoices)
                          }
                        >
                          Submit
                        </Button>
                      </Box>
                    </>
                  ) : props.isAwardedEmpReadOnlyView ? (
                    <>
                      <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
                        <Button
                          sx={{
                            width: "100px",
                          }}
                          variant="outlined"
                          color="primary"
                          onClick={props.handleManageShiftBidBackClick}
                        >
                          Back
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
                        <Button
                          sx={{ width: "100px" }}
                          variant="outlined"
                          color="primary"
                          onClick={handleClickCancelDialogOpen}
                        >
                          Cancel
                        </Button>
                      </Box>
                      <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
                        <Button
                          sx={{
                            width: "100px",
                            backgroundColor: "secondary.swaGray",
                            "&:hover": {
                              backgroundColor: "secondary.swaGray5",
                            },
                          }}
                          variant="contained"
                          onClick={handleClickGuideDialogOpen}
                        >
                          Guide
                        </Button>
                      </Box>
                      <Box sx={{ paddingTop: "10px" }}>
                        <Button
                          sx={{ width: "100px" }}
                          variant="contained"
                          color="primary"
                          onClick={handleReviewButtonClick}
                        >
                          Next
                        </Button>
                      </Box>
                    </>
                  )}
                </Grid>
              </Stack>
            </Grid>
            {props.selectedBidGroupEmployeeData && (
              <Grid
                container
                sx={{
                  pt: 3,
                  paddingLeft: "2rem",
                }}
                spacing={2}
              >
                <Grid item md={4}>
                  <div style={{ display: "flex" }}>
                    <div>
                      <TextField
                        style={{ maxWidth: "17vw", margin: "auto", textAlign: "left", width: "90px" }}
                        label={STATION}
                        variant="outlined"
                        value={props.station}
                        disabled
                      />
                    </div>
                    <div style={{ paddingLeft: "10px" }}>
                      <TextField
                        className={classes.inputText}
                        label={EMPLOYEE_ID}
                        variant="outlined"
                        value={props.selectedBidGroupEmployeeData?.eid}
                        disabled
                      />
                    </div>
                    <div>
                      <Button
                        data-testid="myPermanentBid-view-button"
                        style={{ padding: "15px" }}
                        variant="contained"
                        color="primary"
                        disabled
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </Grid>
                <Grid
                  item
                  md={8}
                  sx={{
                    paddingRight: "3.2rem",
                  }}
                >
                  {props.selectedBidGroupEmployeeData ? (
                    <div>
                      <Grid
                        container
                        sx={{
                          backgroundColor: "secondary.swaGray1",
                          height: "auto",
                          borderRadius: "5px",
                          marginLeft: "20px",
                        }}
                      >
                        <Grid
                          item
                          xs={4}
                          style={{ fontSize: "24px", paddingTop: "11px", paddingLeft: "16px", textAlign: "left" }}
                        >
                          {props.selectedBidGroupEmployeeData.lastName +
                            ", " +
                            props.selectedBidGroupEmployeeData.shortName}
                        </Grid>
                        <Grid item xs={8} sx={{ textAlign: "left", maxHeight: "56px" }}>
                          <Stack direction="row">
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                HOME STATION
                              </Typography>
                              <p className={classes.employeeDataValue}>
                                {props.selectedBidGroupEmployeeData.homeStation}
                              </p>
                            </Box>
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                WORKGROUP
                              </Typography>
                              <p className={classes.employeeDataValue}>
                                {getWorkgroupGivenJobCode(props.selectedBidGroupEmployeeData.homeAccountJobCode)}
                              </p>
                            </Box>
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                STATUS
                              </Typography>
                              <p className={classes.employeeDataValue}>{props.selectedBidGroupEmployeeData.ftpt}</p>
                            </Box>
                          </Stack>
                        </Grid>
                      </Grid>
                    </div>
                  ) : (
                    ""
                  )}
                </Grid>
              </Grid>
            )}
            <Box
              sx={{
                width: "100%",
                margin: "auto",
                paddingLeft: "2rem",
                paddingRight: "2rem",
                marginTop: "1rem",
              }}
            >
              {isReviewPage || props.isAwardedEmpReadOnlyView ? (
                <>
                  <Grid
                    sx={{
                      border: "solid 1px",
                      borderColor: "secondary.swaGray2",
                      backgroundColor: "secondary.swaLightestBlue",
                      paddingLeft: "1rem",
                      textAlign: "left",
                    }}
                  >
                    <Grid>
                      <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
                        Review your {selectedChoices?.length} choices
                      </Typography>
                    </Grid>
                    {!props.isAwardedEmpReadOnlyView && (
                      <Grid sx={{ marginLeft: "5px" }}>
                        <li> You can use the up/down arrows to reorder your choices</li>
                      </Grid>
                    )}
                  </Grid>
                  <Grid
                    container
                    sx={{
                      border: "solid 1px",
                      borderColor: "secondary.swaGray2",
                    }}
                  >
                    <Box
                      sx={{
                        height: `calc(85vh - 250px)`,
                        width: "inherit",
                        "& .choices.selected": { backgroundColor: "secondary.swaLightestBlue" },
                        "& .add-remove-icons.blocked": { backgroundColor: "secondary.swaGray2" },
                        "& .add-remove-icons.addIcon": { backgroundColor: "secondary.swaBlue" },
                      }}
                    >
                      <ShiftBidReviewTable
                        selectedBidLines={selectedBidLines}
                        setSelectedChoices={setSelectedChoices}
                        setSelectedBidLines={setSelectedBidLines}
                        selectedChoices={selectedChoices}
                        isBidLinesLoading={isBidLinesLoading}
                        isDesktopView={true}
                        isReadOnly={props.isAwardedEmpReadOnlyView}
                        selectedAwardedBid={props.selectedAwardedBid}
                        useAssignmentForEmp={props.useAssignmentForEmp}
                      />
                    </Box>
                  </Grid>
                </>
              ) : (
                <>{renderBidLines(false)}</>
              )}
            </Box>

            {/* Dialogs */}
            <ConfirmationDialog
              isDialogOpen={isExitDialogOpen}
              title="Cancel"
              message={BID_FORM_CANCEL_MESSAGE}
              handleNoButtonClicked={handleClickCancelDialogClose}
              handleYesButtonClicked={() => props.setIsManageShiftBidChoicesSelected(false)}
            />
            {renderSubmitShiftBidChoiceDialog()}
            {viewCommentsDialogOpen && (
              <ViewCommentsDialog
                isDialogOpen={viewCommentsDialogOpen}
                handleClickDialogClose={() => setViewCommentsDialogOpen(false)}
                header={`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} on `}
                publishedTimestamp={getLastUpdatedTimeString()}
                message={lastUpdatedComments}
              />
            )}
            <InfoDialog
              isDialogOpen={isGuideDialogOpen}
              title="Guide"
              message={BID_FORM_GUIDE_MESSAGE()}
              handleCloseClicked={handleCloseClickedGuideDialog}
              isDesktopView={props.isDesktopView}
            />
            <SuccessDialog
              isSuccessDialogOpen={isSubmitResponseDialogOpen}
              title={SHIFT_BID_CHOICE_CONFIRMATION_TITLE}
              message={SHIFT_BID_CHOICE_CONFIRMATION_MESSAGE}
              hasError={submitErrors !== ""}
              error={BID_CHOICE_SAVE_ERROR_MESSAGE}
              handleOKClick={() => {
                setIsReviewPage(false);
                setIsSubmitResponseDialogOpen(false);
                props.setIsManageShiftBidChoicesSelected(false);
              }}
            />
          </Grid>
        </>
      );
    }
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div className="App-contents enable-scroll" data-testid="manage-shift-bid-choices-page">
      {isUserAuthorizedStateLoading ? <PageLoading /> : renderShiftBidChoicesPage()}
    </div>
  );
};

ManageShiftBidChoices.propTypes = {
  selectedBidGroupEmployeeData: PropTypes.object.isRequired,
  referenceNumber: PropTypes.string.isRequired,
  featureConfig: PropTypes.object.isRequired,
  setIsManageShiftBidChoicesSelected: PropTypes.func,
  station: PropTypes.string.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
  useAssignmentForEmp: PropTypes.bool,
  isAwardedEmpReadOnlyView: PropTypes.bool,
  setIsBidGroupTabChanged: PropTypes.func,
  selectedAwardedBid: PropTypes.object,
  handleManageShiftBidBackClick: PropTypes.func,
};

export default ManageShiftBidChoices;
